import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import {
  BaseChatState,
  BotAgentStage,
  CartPreview,
  CheckoutReady,
  ProductDetailsPayload,
  ProductImagesPayload,
  SuggestedReply,
  UIMessage,
} from '../types/chat.types';

/** Sideband bundle attached to bot:done, dispatched in one FLUSH_MESSAGE action. */
export interface ChatTurnSideband {
  searchResults?: { tool: string; results: any[]; items?: any[] };
  productDetails?: ProductDetailsPayload;
  productImages?: ProductImagesPayload;
  cartPreview?: CartPreview;
  checkoutReady?: CheckoutReady;
  suggestedReplies?: SuggestedReply[];
  currentStage?: BotAgentStage;
}

export type ChatAction =
  | { type: 'ADD_MESSAGE'; message: UIMessage }
  | { type: 'SET_MESSAGES'; messages: UIMessage[] }
  | { type: 'APPEND_CHUNK'; text: string }
  | { type: 'FLUSH_MESSAGE'; sideband?: ChatTurnSideband }
  | { type: 'SET_TOOL_LABEL'; label: string | null }
  | { type: 'SET_SOCKET_STATUS'; status: BaseChatState['socketStatus'] }
  | { type: 'SET_OPEN'; isOpen: boolean }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'CLEAR_SUGGESTED_REPLIES' }
  | { type: 'SET_CURRENT_STAGE'; stage: BotAgentStage | null }
  | {
      type: 'SET_LIST_SELECTION';
      messageId: string;
      selection: { key: string | number; label: string };
    }
  | { type: 'RESET' };

const initialState: BaseChatState = {
  messages: [],
  socketStatus: 'disconnected',
  streamingText: '',
  isStreaming: false,
  activeToolLabel: null,
  isOpen: false,
  isLoadingHistory: false,
  suggestedReplies: [],
  currentStage: null,
};

function chatReducer(state: BaseChatState, action: ChatAction): BaseChatState {
  switch (action.type) {
    case 'ADD_MESSAGE':
      // A new user message replaces any stale suggested-reply chips and
      // signals that this turn's sideband (cart_preview, etc.) is now history.
      return {
        ...state,
        messages: [...state.messages, action.message],
        suggestedReplies: action.message.role === 'user' ? [] : state.suggestedReplies,
      };
    case 'SET_MESSAGES':
      return { ...state, messages: action.messages };
    case 'APPEND_CHUNK':
      return { ...state, isStreaming: true, streamingText: state.streamingText + action.text };
    case 'FLUSH_MESSAGE': {
      const sb = action.sideband ?? {};
      const nextSuggested = sb.suggestedReplies ?? state.suggestedReplies;
      const nextStage = sb.currentStage ?? state.currentStage;
      // Normally an empty-text turn produces no bubble. But a checkout-ready
      // turn must still surface a message so its checkoutReady sideband (and the
      // in-text checkout link rendered from it) is never dropped — even on the
      // rare turn where the LLM emitted no closing prose.
      if (!state.streamingText && !sb.checkoutReady?.ready) {
        return {
          ...state,
          isStreaming: false,
          streamingText: '',
          activeToolLabel: null,
          suggestedReplies: nextSuggested,
          currentStage: nextStage,
        };
      }
      const msg: UIMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        text: state.streamingText,
        timestamp: new Date().toISOString(),
        searchResults: sb.searchResults,
        productDetails: sb.productDetails,
        productImages: sb.productImages,
        cartPreview: sb.cartPreview,
        checkoutReady: sb.checkoutReady,
      };
      return {
        ...state,
        messages: [...state.messages, msg],
        streamingText: '',
        isStreaming: false,
        activeToolLabel: null,
        suggestedReplies: nextSuggested,
        currentStage: nextStage,
      };
    }
    case 'SET_TOOL_LABEL':
      return { ...state, activeToolLabel: action.label };
    case 'SET_SOCKET_STATUS':
      return { ...state, socketStatus: action.status };
    case 'SET_OPEN':
      return { ...state, isOpen: action.isOpen };
    case 'SET_LOADING':
      return { ...state, isLoadingHistory: action.loading };
    case 'CLEAR_SUGGESTED_REPLIES':
      return { ...state, suggestedReplies: [] };
    case 'SET_CURRENT_STAGE':
      return { ...state, currentStage: action.stage };
    case 'SET_LIST_SELECTION':
      // Record which option the user picked on a given turn's option list, so
      // that once the list is superseded it can collapse to a marked header.
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.messageId
            ? { ...m, listSelection: action.selection }
            : m,
        ),
      };
    case 'RESET':
      // Preserve `isOpen` so tab switches (which RESET chat state) don't
      // close the drawer. Same for `socketStatus` — the active connection
      // can keep flowing; the BotWidget useEffect re-binds on sessionId
      // change.
      return {
        ...initialState,
        isOpen: state.isOpen,
        socketStatus: state.socketStatus,
      };
    default:
      return state;
  }
}

interface ChatContextValue {
  state: BaseChatState;
  dispatch: React.Dispatch<ChatAction>;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  return <ChatContext.Provider value={{ state, dispatch }}>{children}</ChatContext.Provider>;
};

export function useChatContext(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be inside ChatProvider');
  return ctx;
}
